// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@eisinc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { headingTexts, mobileHeadingTexts } from "@eisinc/App/Widgets/DesignSystem/DummyData/DummyData";
import typography, { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

interface TypographyTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    fontItem?: TypographyProps;
    linkWrapper?: InjectableCss;
    typographyWrapper?: GridContainerProps;
    viewTitle?: TypographyProps;
    headLine?: TypographyProps;
    headLineMobile?: TypographyProps;
    regular?: TypographyProps;
    small?: TypographyProps;
    xSmall?: TypographyProps;
    xxSmall?: TypographyProps;
    primaryHeading?: TypographyProps;
    listStyle?: InjectableCss;
}

const typogrphyTabStyles: TypographyTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 15px;

            ${xScreen("gte", "md")} {
                &&& {
                    margin-bottom: 40px;
                }
            }
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 15px;
            padding-right: 11px;

            &.rightList {
                padding-left: 11px;
                padding-right: 0;
            }
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            width: 100%;
            flex-wrap: wrap;
            gap: 58px;
        `,
    },
    fontItem: {
        css: css`
            min-width: 168px;
        `,
    },
    typographyWrapper: {
        gap: 22,
        css: css`
            ${GridItemStyle} {
                > * {
                    margin-bottom: 0;
                }
            }
        `,
    },
    viewTitle: {
        variant: "h3",
        css: css`
            padding-top: 8px;
            border-top: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            background-color: ${color("common.ui2")};
            width: 100%;
            padding: 12px;
            font-size: ${fontConfig.regular.size}px;
            line-height: ${fontConfig.regular.lineHeight};
        `,
    },
    headLine: {
        size: fontConfig.headLine.size,
        lineHeight: fontConfig.headLine.lineHeight,
        weight: fontConfig.weight.medium,
        css: css`
            display: block;
            margin-bottom: 24px;
        `,
    },
    regular: {
        size: fontConfig.regular.size,
        lineHeight: fontConfig.regular.lineHeight,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 16px;
        `,
    },
    small: {
        size: fontConfig.small.size,
        lineHeight: fontConfig.small.lineHeight,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 14px;
        `,
    },
    xSmall: {
        size: fontConfig.xSmall.size,
        lineHeight: fontConfig.xSmall.lineHeight,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 12px;
        `,
    },
    xxSmall: {
        size: fontConfig.xxSmall.size,
        lineHeight: fontConfig.xxSmall.lineHeight,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 10px;
        `,
    },
    headLineMobile: {
        size: fontConfig.headLineMobile.size,
        lineHeight: fontConfig.headLineMobile.lineHeight,
        weight: fontConfig.weight.medium,
    },
    primaryHeading: {
        color: "primary.main",
    },
    listStyle: {
        css: css`
            &&& {
                padding: 0 0 0 20px;
                margin: 0;
            }
        `,
    },
};

const styles = typogrphyTabStyles;

const bodyCopy = [
    {
        title: "Body Copy : Paragraph : Regular",
        style: styles.regular,
        fontInfo: `Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.regular.size}px : Line-Height: ${fontConfig.regular.lineHeight}`,
    },
    {
        title: "Body Copy : Paragraph : Small",
        style: styles.small,
        fontInfo: `Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.small.size}px : Line-Height:  ${fontConfig.small.lineHeight}`,
    },
    {
        title: "Body Copy : Paragraph : xSmall",
        style: styles.xSmall,
        fontInfo: `Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.xSmall.size}px : Line-Height: ${fontConfig.xSmall.lineHeight}`,
    },
    {
        title: "Body Copy : Paragraph : xxSmall",
        style: styles.xxSmall,
        fontInfo: `Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.xxSmall.size}px : Line-Height: ${fontConfig.xxSmall.lineHeight}`,
    },
];

const TypographyTab: FC = props => {
    return (
        <SectionContainer extendedStyles={{ gridContainer: { gap: 0 } }}>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Font Families")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <StyledWrapper {...styles.formWrapper}>
                    <Typography {...styles.fontItem}>{translate("Gibson")}</Typography>
                    {Object.entries(fontConfig.weight).map(([key, value]) => (
                        <Typography {...styles.fontItem} key={key} weight={value}>
                            {capitalize(key)}
                        </Typography>
                    ))}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Hierarchy")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <GridContainer {...styles.typographyWrapper}>
                    <GridItem width={2}></GridItem>
                    <GridItem width={5}>
                        <Typography {...styles.viewTitle}>{translate("Desktop")}</Typography>
                    </GridItem>
                    <GridItem width={5}>
                        <Typography {...styles.viewTitle}>{translate("Responsive")}</Typography>
                    </GridItem>
                    {Object.keys(headingTexts).map((key: any) => (
                        <React.Fragment key={key}>
                            <GridItem width={2}>
                                <Typography variant={key}>{translate(key.toUpperCase())}</Typography>
                            </GridItem>
                            <GridItem width={5}>{headingTexts[key].desktop}</GridItem>
                            <GridItem width={5}>{mobileHeadingTexts[key]?.mobile}</GridItem>
                        </React.Fragment>
                    ))}
                </GridContainer>
            </GridItem>
            {bodyCopy.map(({ title, style, fontInfo }) => (
                <GridItem {...styles.gridItem} key={title}>
                    <SectionTitle>{translate(title)}</SectionTitle>
                    <Typography {...style}>{fontInfo}</Typography>
                    <Typography {...style}>
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Typography {...style}>
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Link typographyProps={{ ...style, weight: fontConfig.weight.medium }}>Text Link : Inactive</Link>
                    <Link color="common.ui8" typographyProps={{ ...style, weight: fontConfig.weight.medium }}>
                        Text Link : Hover
                    </Link>
                </GridItem>
            ))}
            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Body Copy : Single")}</SectionTitle>
                <Typography {...styles.regular}>
                    {`Regular : Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.singleLineRegular.size}px : Line-Height: ${fontConfig.singleLineRegular.lineHeight}`}
                    <br /> [Example Second Line]
                </Typography>
                <Typography {...styles.small}>
                    {`Small : Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.small.size}px : Line-Height: ${fontConfig.singleLineSmall.lineHeight} `}
                    <br />
                    [Example Second Line]
                </Typography>
                <Typography {...styles.xSmall}>
                    {`XSmall : Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.xSmall.size}px : Line-Height: ${fontConfig.singleLinexSmall.lineHeight}`}
                    <br />
                    [Example Second Line]
                </Typography>
                <Typography {...styles.xxSmall}>
                    {`XXSmall : Gibson : ${fontConfig.weight.regular} : Font-Size ${fontConfig.xxSmall.size}px : Line-Height: ${fontConfig.singleLinexxSmall.lineHeight}`}
                    <br />
                    [Example Second Line]
                </Typography>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Headers + Paragraph Spacing</SectionTitle>
                <Typography variant="h1" {...styles.primaryHeading}>
                    {translate("H1 Example")}
                </Typography>
                <Typography variant="h2" {...styles.primaryHeading}>
                    {translate("H2 Example")}
                </Typography>
                <Typography {...styles.regular}>
                    Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore
                    et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
                    aliquip ex ea commodo consequat.
                </Typography>
                <Typography {...styles.regular}>
                    Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore
                    et dolore magna aliqua.
                </Typography>
                <Typography variant="h3" {...styles.primaryHeading}>
                    {translate("H3 Example")}
                </Typography>
                <Typography {...styles.regular}>
                    Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore
                    et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
                    aliquip ex ea commodo consequat.
                </Typography>
                <Typography variant="h4" {...styles.primaryHeading}>
                    {translate("H4 Example")}
                </Typography>
                <Typography variant="h5" {...styles.primaryHeading}>
                    {translate("H5 Example")}
                </Typography>
                <Typography variant="h6" {...styles.primaryHeading}>
                    {translate("H6 Example")}
                </Typography>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Bulleted Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList {...styles.listStyle}>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>
                            Unordered List Item w/ Sub-List
                            <UnorderedList>
                                <ListItem>Unordered Sub-List Item</ListItem>
                                <ListItem>Unordered Sub-List Item</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>Unordered List Item</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem} className="rightList">
                <SectionTitle>Numbered Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList {...styles.listStyle}>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>
                            Numbered List Item w/ Sub-List
                            <OrderedList>
                                <ListItem>Numbered Sub-List Item</ListItem>
                                <ListItem>Numbered Sub-List Item</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>Numbered List Item</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default TypographyTab;
